/**
 * The logistics questionnaire as the console shows it (Traci, 28 Sep 2026). Pure.
 *
 * Order: coverage start, coverage end, then the starting location and any more locations, the way the client fills it
 * in on the portal. Getting ready is left out: it is now an hour on us, so the start and end times say it all. Times
 * read in 12-hour AM/PM, not 24-hour.
 */

/** "16:30" -> "4:30 PM". Anything that is not an HH:MM time comes back unchanged. */
export function to12h(v) {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(v ?? '').trim())
  if (!m) return v
  const h = Number(m[1])
  if (h > 23) return v
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`
}

import { VENDOR_TYPES, vendorRows, vendorsToSave } from '../logistics/vendors.js'

const HIDDEN = new Set(['getting_ready_time', 'getting_ready_location', 'getting_ready_notes', 'locations', 'vendors'])
const LEADING = [
  ['coverage_start_time', 'Start time'],
  ['coverage_end_time', 'End time']
]
const TRAILING = [
  ['person1_first_name', 'Person 1 first name'],
  ['person1_last_name', 'Person 1 last name'],
  ['person2_first_name', 'Person 2 first name'],
  ['person2_last_name', 'Person 2 last name'],
  ['wedding_date', 'Wedding date'],
  ['coverage_hours', 'Coverage hours'],
  ['city', 'City'],
  ['state', 'State'],
  ['first_look_choice', 'First look'],
  ['first_look_location', 'First look location'],
  ['number_of_guests', 'Number of guests'],
  ['day_of_contact_name', 'Day-of contact'],
  ['day_of_contact_phone', 'Day-of contact phone'],
  ['must_have_photos', 'Must-have photos'],
  ['venue_restrictions', 'Venue restrictions'],
  ['special_requests', 'Special requests']
]
const humanize = k => String(k).replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())
const shown = v => v !== null && v !== undefined && v !== '' && typeof v !== 'object'
const value = (k, v) => (/_time$/.test(k) ? to12h(v) : String(v))

/**
 * @returns {{ times: {key,label,value}[], locations: {label,address}[], rest: {key,label,value}[] }}
 *   times first, then locations (the first is the starting location), then everything else in a fixed order and any
 *   field this list does not know yet at the end.
 */
export function questionnaireView(answers) {
  const a = answers && typeof answers === 'object' ? answers : {}
  const row = ([k, label]) => (shown(a[k]) ? { key: k, label, value: value(k, a[k]) } : null)
  const known = new Set([...LEADING, ...TRAILING].map(([k]) => k))
  const times = LEADING.map(row).filter(Boolean)
  const rest = [
    ...TRAILING.map(row).filter(Boolean),
    ...Object.keys(a).filter(k => !known.has(k) && !HIDDEN.has(k) && shown(a[k])).map(k => ({ key: k, label: humanize(k), value: value(k, a[k]) }))
  ]
  const locations = (Array.isArray(a.locations) ? a.locations : [])
    .map((l, i) => ({ label: i === 0 ? 'Starting location' : `Location ${i + 1}`, name: l?.label || l?.name || l?.type || null, address: l?.address || null, time: l?.time ? to12h(l.time) : null }))
    .filter(l => l.name || l.address)
  // Wedding vendors (2 Nov 2026), in the questionnaire's order
  const vendors = vendorsToSave(vendorRows(a.vendors)).map(v => ({ label: VENDOR_TYPES.find(t => t.type === v.type)?.label || v.type, value: [v.contact_name, v.email, v.phone].filter(Boolean).join(' · ') }))
  return { times, locations, rest, vendors }
}
